import type { MemberDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { Search, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { ConfirmDialog } from '../../components/Modal';
import { SettingsHeader } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { membersApi, serversApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';

/** Tab „Besitz übertragen" (nur Owner): Mitglied suchen, mit Passwort bestätigen. */
export function TransferTab({ serverId }: { serverId: string }) {
  const navigate = useNavigate();
  const server = useAppStore((s) => s.servers[serverId]);
  const me = useAppStore((s) => s.me);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [target, setTarget] = useState<MemberDto | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);
  const q = useQuery({
    queryKey: ['transfer-candidates', serverId, debounced],
    queryFn: () => membersApi.list(serverId, { q: debounced || undefined, limit: 25 }),
    enabled: debounced.length > 0,
  });
  const candidates = (q.data?.items ?? []).filter((m) => m.user.id !== me?.id && !m.user.isBot);
  if (!server || !me) return null;

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const dto = await serversApi.transfer(serverId, { userId: target.user.id, password: me.hasPassword ? password : undefined });
      useAppStore.getState().dispatch('SERVER_UPDATE', dto);
      toast.success(`${displayName(target.user, target)} besitzt jetzt „${server.name}"`);
      void navigate(paths.server(serverId));
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Besitz übertragen">
        Der neue Besitzer erhält alle Rechte – auch das Recht, dich zu entfernen oder den Server zu löschen. Du kannst das nicht selbst rückgängig machen.
      </SettingsHeader>
      <div className="search-input">
        <Search size={16} aria-hidden="true" />
        <input className="input" placeholder="Mitglied suchen" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Mitglied suchen" data-testid="transfer-search" />
      </div>
      {candidates.length > 0 && (
        <ul className="settings-list" data-testid="transfer-candidates">
          {candidates.map((m) => (
            <li key={m.user.id} className="settings-row">
              <Avatar user={m.user} size={32} />
              <div className="member-settings-main">
                <strong className="truncate">{displayName(m.user, m)}</strong>
                <span className="muted small">@{m.user.username}</span>
              </div>
              <button className="btn btn-sm btn-danger" onClick={() => setTarget(m)} data-testid="transfer-pick">
                Übertragen …
              </button>
            </li>
          ))}
        </ul>
      )}
      {debounced && !q.isPending && candidates.length === 0 && <p className="muted">Keine passenden Mitglieder.</p>}
      {target && (
        <ConfirmDialog
          title="Besitz übertragen?"
          message={
            <>
              <strong>{displayName(target.user, target)}</strong> wird Besitzer von „{server.name}".
            </>
          }
          confirmLabel="Besitz übertragen"
          danger
          busy={busy || (me.hasPassword && !password)}
          onConfirm={() => void submit()}
          onClose={() => {
            setTarget(null);
            setError(null);
          }}
        >
          {me.hasPassword && (
            <div className="field">
              <label htmlFor="transfer-password">Dein Passwort</label>
              <input id="transfer-password" type="password" className="input" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="transfer-password" />
            </div>
          )}
          {error && (
            <p className="error-text" role="alert" data-testid="transfer-error">
              {error}
            </p>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}

/** Tab „Server löschen" (nur Owner): Servername eintippen und mit Passwort bestätigen. */
export function DeleteServerTab({ serverId }: { serverId: string }) {
  const navigate = useNavigate();
  const server = useAppStore((s) => s.servers[serverId]);
  const me = useAppStore((s) => s.me);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!server || !me) return null;
  const ok = name.trim() === server.name.trim() && (!me.hasPassword || password.length > 0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await serversApi.remove(serverId, me.hasPassword ? password : undefined);
      void navigate(paths.home(), { replace: true });
      useAppStore.getState().dispatch('SERVER_DELETE', { id: serverId, reason: 'deleted' });
      toast.success(`„${server.name}" wurde gelöscht`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <form className="stack settings-stack" onSubmit={submit}>
      <SettingsHeader title="Server löschen" />
      <p className="notice danger" role="note">
        <TriangleAlert size={18} aria-hidden="true" />
        Alle Kanäle, Nachrichten, Rollen, Emojis und Einladungen von „{server.name}" werden endgültig gelöscht. Das lässt sich nicht rückgängig machen.
      </p>
      {server.isMirrored && <p className="muted">Gespiegelte Server können nur über die Admin-Brücke entfernt werden.</p>}
      <div className="field">
        <label htmlFor="delete-server-name">Gib zur Bestätigung den Servernamen ein</label>
        <input id="delete-server-name" className="input" value={name} placeholder={server.name} onChange={(e) => setName(e.target.value)} autoComplete="off" data-testid="delete-server-name" />
      </div>
      {me.hasPassword && (
        <div className="field">
          <label htmlFor="delete-server-password">Dein Passwort</label>
          <input
            id="delete-server-password"
            type="password"
            className="input"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="delete-server-password"
          />
        </div>
      )}
      {error && (
        <p className="error-text" role="alert" data-testid="delete-server-error">
          {error}
        </p>
      )}
      <div>
        <button className="btn btn-danger" disabled={!ok || busy || server.isMirrored} data-testid="delete-server-submit">
          {busy ? 'Wird gelöscht …' : 'Server endgültig löschen'}
        </button>
      </div>
    </form>
  );
}
